import React from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Grid,
  Avatar,
  Typography,
  Box,
  Button,
  Modal,
  Stack,
  Divider,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { Person, Star, Delete, CalendarToday, AccessTime, LocationOn, Message } from "@mui/icons-material";
import Loading from "../../components/loading/loading";
import { useSelector, useDispatch } from "react-redux";
import { deleteRequest } from "../../state/slices/studnent";
import dayjs from "dayjs";
import { convertTo12Hour } from "../../utils/timeFormat";

const CustomCard = ({
  requests,
  loading,
  value,
  ReviewModal,
  handlModaleOpen,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const userState = useSelector((state) => state.user).user;
  const isAdmin = userState?.roles?.includes('ADMIN');
  const [modal, setModal] = React.useState(false);
  const [selectedRequest, setSelecteRequest] = React.useState(null);
  const dispatch = useDispatch();

  const val = {
    0: t('studentRequest.pending'),
    1: t('studentRequest.approved'),
  };

  const handleCloseModal = () => setModal(false);

  const ModalPage = ({ modal, handleCloseModal }) => {
    if (!selectedRequest) return null;
    const request = selectedRequest;

    return (
      <Modal 
        open={modal} 
        onClose={handleCloseModal}
        BackdropProps={{
          sx: {
            bgcolor: alpha('#000', 0.5),
            backdropFilter: 'blur(4px)',
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 600 },
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
            p: 4,
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 2,
                bgcolor: theme.palette.primary.main,
              }}
            >
              {request.tutorImageUrl ? (
                <img
                  src={request.tutorImageUrl}
                  alt="tutor"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Person sx={{ width: 60, height: 60 }} />
              )}
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
              {request.tutorName}
            </Typography>
            <Chip
              label={request.status}
              color={request.status === 'Approved' ? 'success' : 'warning'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {t('dashboard.requestDate')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {new Date(request.date).toLocaleString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>

            {request.startingTime && !request.startTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {t('dashboard.startingTime')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {request.startingTime}
                  </Typography>
                </Box>
              </Box>
            )}

            {request.preferredDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {t('dashboard.preferredDate')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {new Date(request.preferredDate).toLocaleDateString(undefined, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>
            )}

            {request.startTime && request.endTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {t('dashboard.timeWindow') || 'Time Window'}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {convertTo12Hour(request.startTime)} - {convertTo12Hour(request.endTime)}
                  </Typography>
                </Box>
              </Box>
            )}

            {request.duration && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {t('dashboard.duration')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {request.duration === 'ONE_TIME' ? t('home.oneTime') : t('home.recurring')}
                  </Typography>
                </Box>
              </Box>
            )}

            {request.tutoringType && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {t('dashboard.tutoringType')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {request.tutoringType?.includes("REMOTE") && t('common.online')}
                    {request.tutoringType?.includes("IN_PERSON") && ' ' + t('common.inPerson')}
                  </Typography>
                </Box>
              </Box>
            )}

            {request.message && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Message sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                    {t('dashboard.message')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>
                    {request.message}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Only show Admin Status and Tutor Status for ADMIN users */}
            {isAdmin && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                <Chip
                  label={`${t('dashboard.adminStatus')}: ${request.adminApproved || 'N/A'}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${t('dashboard.tutorStatus')}: ${request.status || 'N/A'}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              sx={{ borderRadius: 2 }}
            >
              {t('dashboard.close')}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const handleCardClick = (event, request) => {
    if (event.target.tagName === 'BUTTON' || event.target.closest('button')) {
      return;
    }
    setSelecteRequest(request);
    setModal(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loading />
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t('dashboard.noRequests')} {val[value]}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {value === 0
            ? t('studentRequest.noPendingRequests')
            : t('studentRequest.noApprovedRequests')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ModalPage modal={modal} handleCloseModal={handleCloseModal} />
      <Grid container spacing={3}>
        {requests.map((request) => (
          <Grid item xs={12} sm={6} md={4} key={request._id}>
            <ReviewModal tutorId={request.tutorId} />
            <Card
              onClick={(e) => handleCardClick(e, request)}
              sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderColor: theme.palette.primary.main,
                }
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: theme.palette.primary.main,
                      mr: 2,
                    }}
                  >
                    {request.tutorImageUrl ? (
                      <img
                        src={request.tutorImageUrl}
                        alt="tutor"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Person sx={{ width: 35, height: 35 }} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mb: 0.5,
                      }}
                    >
                      {request.tutorName}
                    </Typography>
                    <Chip
                      label={request.status}
                      size="small"
                      color={request.status === 'Approved' ? 'success' : 'warning'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {new Date(request.date).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  {value === 0 && (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<Delete />}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(deleteRequest(request.requestId));
                      }}
                      sx={{
                        flex: 1,
                        borderRadius: 2,
                        textTransform: 'capitalize',
                        fontWeight: 600,
                      }}
                    >
                      {t('dashboard.delete')}
                    </Button>
                  )}
                  {value === 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<Star />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal(false);
                        handlModaleOpen();
                      }}
                      sx={{
                        flex: 1,
                        borderRadius: 2,
                        textTransform: 'capitalize',
                        fontWeight: 600,
                      }}
                    >
                      {t('studentRequest.addReview')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CustomCard;
